/**
 * Meilisearch Search Results Module
 * Replaces Shopify's native search results on /search?q=... pages
 * with Meilisearch-powered results using Section Rendering API for product cards.
 */

import { isMeiliEnabled, getMeiliConfig, meiliSearch, escapeHtml } from "./meilisearch-client";

declare global {
  interface Window {
    finishSelector?: {
      updateCollectionPrices: () => void;
    };
  }
}

// Products per page
const PRODUCTS_PER_PAGE = 24;

// Cache for AJAX-loaded product cards
const ajaxCardCache = new Map<string, string>();

/**
 * Get search query from URL
 */
function getSearchQuery(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("q")?.trim() || "";
}

/**
 * Get current page from URL
 */
function getCurrentPage(): number {
  const params = new URLSearchParams(window.location.search);
  return parseInt(params.get("page") || "1", 10);
}

/**
 * Fetch a product card via Shopify's Section Rendering API
 */
async function fetchProductCard(handle: string): Promise<string | null> {
  if (ajaxCardCache.has(handle)) {
    return ajaxCardCache.get(handle) || null;
  }

  try {
    const response = await fetch(`/products/${handle}?section_id=product-card-ajax`);
    if (!response.ok) return null;
    const html = await response.text();
    ajaxCardCache.set(handle, html);
    return html;
  } catch {
    return null;
  }
}

/**
 * Fetch multiple product cards in parallel
 */
async function fetchProductCards(handles: string[]): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  await Promise.all(
    handles.map(async (handle) => {
      const html = await fetchProductCard(handle);
      if (html) results.set(handle, html);
    })
  );
  return results;
}

/**
 * Extract the <li> card element from Section Rendering API response
 */
function extractCardHtml(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  let el = template.content.firstElementChild;

  // Shopify wraps in <div class="shopify-section"> — extract the inner <li>
  if (el && el.classList.contains("shopify-section")) {
    el = el.querySelector(".grid__item") || el.firstElementChild;
  }

  return el ? el.outerHTML : "";
}

/**
 * Render pagination controls
 */
function renderPagination(totalHits: number, currentPage: number, query: string): string {
  const totalPages = Math.ceil(totalHits / PRODUCTS_PER_PAGE);
  if (totalPages <= 1) return "";

  const items: string[] = [];
  const q = encodeURIComponent(query);

  // Previous
  if (currentPage > 1) {
    items.push(
      `<li><a href="/search?q=${q}&page=${currentPage - 1}" class="pagination__item link" aria-label="Previous page">&laquo;</a></li>`
    );
  }

  // Page numbers (show max 7 pages with ellipsis)
  const start = Math.max(1, currentPage - 3);
  const end = Math.min(totalPages, currentPage + 3);

  if (start > 1) {
    items.push(`<li><a href="/search?q=${q}&page=1" class="pagination__item link">1</a></li>`);
    if (start > 2) {
      items.push(`<li><span class="pagination__item">&hellip;</span></li>`);
    }
  }

  for (let i = start; i <= end; i++) {
    if (i === currentPage) {
      items.push(
        `<li><span class="pagination__item pagination__item--current" aria-current="page">${i}</span></li>`
      );
    } else {
      items.push(
        `<li><a href="/search?q=${q}&page=${i}" class="pagination__item link">${i}</a></li>`
      );
    }
  }

  if (end < totalPages) {
    if (end < totalPages - 1) {
      items.push(`<li><span class="pagination__item">&hellip;</span></li>`);
    }
    items.push(
      `<li><a href="/search?q=${q}&page=${totalPages}" class="pagination__item link">${totalPages}</a></li>`
    );
  }

  // Next
  if (currentPage < totalPages) {
    items.push(
      `<li><a href="/search?q=${q}&page=${currentPage + 1}" class="pagination__item link" aria-label="Next page">&raquo;</a></li>`
    );
  }

  return `<nav class="pagination-wrapper" role="navigation" aria-label="Pagination"><ul class="pagination__list">${items.join("")}</ul></nav>`;
}

/**
 * Initialize Meilisearch search results page
 */
export async function initSearchResults(): Promise<void> {
  if (!isMeiliEnabled()) return;

  const query = getSearchQuery();
  if (!query) return;

  // Only run on the search page
  if (!window.location.pathname.startsWith("/search")) return;

  const config = getMeiliConfig();
  if (!config) return;

  // Find the product grid container, or create one if Shopify returned 0 native results
  let gridContainer = document.getElementById("product-grid");
  const templateSearch = document.querySelector<HTMLElement>(".template-search");

  if (!gridContainer && templateSearch) {
    // Shopify didn't render the grid because it found 0 results
    // Create the grid container structure
    const wrapper =
      templateSearch.querySelector<HTMLElement>(".product-grid-container") ||
      templateSearch.querySelector<HTMLElement>(".facets-vertical") ||
      templateSearch;

    gridContainer = document.createElement("div");
    gridContainer.className = "template-search__results collection page-width";
    gridContainer.id = "product-grid";
    gridContainer.innerHTML = '<div class="loading-overlay gradient"></div>';
    wrapper.appendChild(gridContainer);
  }

  if (!gridContainer) return;

  // Find or create the grid <ul>
  let grid = gridContainer.querySelector<HTMLElement>("ul.product-grid");

  // Show loading state
  gridContainer.classList.add("loading");

  // Hide the "no results" message from Shopify if present
  const noResultsMsg = templateSearch?.querySelector<HTMLElement>('[role="status"]');

  const currentPage = getCurrentPage();
  const offset = (currentPage - 1) * PRODUCTS_PER_PAGE;

  // Search Meilisearch
  const response = await meiliSearch({
    query,
    limit: PRODUCTS_PER_PAGE,
    offset,
    attributesToRetrieve: ["handle"]
  });

  if (!response || response.hits.length === 0) {
    // No results — show Shopify's native "no results" or our own
    gridContainer.classList.remove("loading");
    if (!grid) return;

    grid.innerHTML = "";

    // Update status message
    if (noResultsMsg) {
      noResultsMsg.textContent = `No results found for "${escapeHtml(query)}". Try a different search term.`;
      noResultsMsg.hidden = false;
    }

    // Remove pagination
    const existingPagination = gridContainer.querySelector(".pagination");
    if (existingPagination) existingPagination.remove();

    return;
  }

  // Update result count status
  const resultCountEl = templateSearch?.querySelector<HTMLElement>('[role="status"]');
  if (resultCountEl) {
    resultCountEl.textContent = `${response.estimatedTotalHits} results for "${escapeHtml(query)}"`;
    resultCountEl.hidden = false;
  }

  // Remove the template-search--empty class since we have results
  if (templateSearch) {
    templateSearch.classList.remove("template-search--empty");
  }

  // Fetch product cards via Section Rendering API
  const handles = response.hits.map((hit) => hit.handle);
  const cards = await fetchProductCards(handles);

  // Create grid if it doesn't exist
  if (!grid) {
    grid = document.createElement("ul");
    grid.className = "grid product-grid grid--2-col-tablet-down grid--4-col-desktop";
    grid.setAttribute("role", "list");

    // Clear any existing content and insert grid
    const loadingOverlay = gridContainer.querySelector(".loading-overlay");
    gridContainer.innerHTML = "";
    if (loadingOverlay) gridContainer.appendChild(loadingOverlay);
    gridContainer.appendChild(grid);
  } else {
    grid.innerHTML = "";
  }

  // Render cards in Meilisearch order
  for (const handle of handles) {
    const html = cards.get(handle);
    if (html) {
      const cardHtml = extractCardHtml(html);
      if (cardHtml) {
        grid.insertAdjacentHTML("beforeend", cardHtml);
      }
    }
  }

  // Update finish-based prices on loaded cards
  if (window.finishSelector) {
    window.finishSelector.updateCollectionPrices();
  }

  // Remove existing pagination
  gridContainer.querySelectorAll(".pagination-wrapper").forEach((el) => el.remove());

  const paginationHtml = renderPagination(response.estimatedTotalHits, currentPage, query);
  if (paginationHtml) {
    grid.insertAdjacentHTML("afterend", paginationHtml);

    // Handle pagination clicks with AJAX instead of full page reload
    const paginationNav = gridContainer.querySelector(".pagination-wrapper");
    if (paginationNav) {
      paginationNav.addEventListener("click", (e) => {
        const link = (e.target as HTMLElement).closest("a");
        if (!link) return;

        e.preventDefault();
        const url = new URL(link.href);
        window.history.pushState({}, "", url.toString());
        initSearchResults();

        // Scroll to top of results
        gridContainer.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  gridContainer.classList.remove("loading");
}

// Handle browser back/forward
window.addEventListener("popstate", () => {
  if (window.location.pathname.startsWith("/search")) {
    initSearchResults();
  }
});
